import {
  CONTRACTABLE_BUFFER,
  PACKAGE_TYPE,
  metaSchemas,
} from "@/lib/constants";
import { gql } from "@apollo/client";
import {
  createPageInfo,
  genres,
  isAllowedRating,
  isDateRangeIn,
  largeThumbnailUrl,
  mediumThumbnailUrl,
  metaResolveType,
  ratingMask,
  smallThumbnailUrl,
  thumbnailUrl,
  values,
} from "./helper";

export const typeDef = gql`
  interface Meta {
    id: ID!
    name: String!
    thumbnailUrl: String
    smallThumbnailUrl: String
    mediumThumbnailUrl: String
    largeThumbnailUrl: String
    header: String
    description: String
    schema_key: String
    copyright: String
    delivery_start_at: String
    delivery_end_at: String
    rights_control_type: String
    parents: [Meta]
    push_rank: String
    rating: String
    isAllowedRating: Boolean
    rights: [Right]
    genres: [Genre]
  }

  type TVShowMeta implements Meta {
    id: ID!
    name: String!
    thumbnailUrl: String
    smallThumbnailUrl: String
    mediumThumbnailUrl: String
    largeThumbnailUrl: String
    header: String
    description: String
    schema_key: String
    copyright: String
    delivery_start_at: String
    delivery_end_at: String
    rights_control_type: String
    parents: [Meta]
    push_rank: String
    rating: String
    isAllowedRating: Boolean
    rights: [Right]
    genres: [Genre]
    production_country: [String]
    cast: String
    staff: String
    production_year: String
    episode_num: Int
    meta_keywords: [String]
    meta_description: String
    channel: Meta
    bookmark: Bookmark
    distributor: String
    course: Course
    rtsid: String
  }

  type SeasonMeta implements Meta {
    id: ID!
    name: String!
    thumbnailUrl: String
    smallThumbnailUrl: String
    mediumThumbnailUrl: String
    largeThumbnailUrl: String
    header: String
    description: String
    schema_key: String
    copyright: String
    delivery_start_at: String
    delivery_end_at: String
    rights_control_type: String
    parents: [Meta]
    push_rank: String
    rating: String
    isAllowedRating: Boolean
    rights: [Right]
    genres: [Genre]
    production_country: [String]
    cast: String
    staff: String
    production_year: String
    episode_count: Int
    meta_keywords: [String]
    meta_description: String
    channel: Meta
  }

  type EpisodeMeta implements Meta {
    id: ID!
    name: String!
    thumbnailUrl: String
    smallThumbnailUrl: String
    mediumThumbnailUrl: String
    largeThumbnailUrl: String
    header: String
    description: String
    schema_key: String
    copyright: String
    delivery_start_at: String
    delivery_end_at: String
    rights_control_type: String
    parents: [Meta]
    push_rank: String
    rating: String
    isAllowedRating: Boolean
    rights: [Right]
    genres: [Genre]
    production_country: [String]
    cast: String
    staff: String
    production_year: String
    distributor: String
    num: Int
    duration: Float
    meta_keywords: [String]
    meta_description: String
    channel: ChannelMeta
    area_control_flag: Boolean
    is_live: Boolean
    tvShow: TVShowMeta
    recommend: Int
    start_over_flag: Boolean
    catch_up_flag: Boolean
    encode_start_date: String
    encode_end_date: String
    resume_point: Int
    last_viewing_at: String
    source_epg_id: String
    plalaFlag: Boolean
    rtsid: String
  }

  type LiveMeta implements Meta {
    id: ID!
    name: String!
    thumbnailUrl: String
    smallThumbnailUrl: String
    mediumThumbnailUrl: String
    largeThumbnailUrl: String
    header: String
    description: String
    schema_key: String
    copyright: String
    delivery_start_at: String
    delivery_end_at: String
    rights_control_type: String
    parents: [Meta]
    push_rank: String
    rating: String
    isAllowedRating: Boolean
    rights: [Right]
    genres: [Genre]
    production_country: [String]
    cast: String
    staff: String
    production_year: String
    distributor: String
    num: Int
    duration: Float
    meta_keywords: [String]
    meta_description: String
    channel: ChannelMeta
    area_control_flag: Boolean
    is_live: Boolean
    tvShow: TVShowMeta
    recommend: Int
    start_over_flag: Boolean
    catch_up_flag: Boolean
    encode_start_date: String
    encode_end_date: String
    resume_point: Int
    last_viewing_at: String
    source_epg_id: String
    plalaFlag: Boolean
    rtsid: String
  }

  type ChannelMetaHeroImage {
    pc: String
    mb: String
  }

  type ChannelMeta implements Meta {
    id: ID!
    name: String!
    thumbnailUrl: String
    smallThumbnailUrl: String
    mediumThumbnailUrl: String
    largeThumbnailUrl: String
    header: String
    description: String
    schema_key: String
    copyright: String
    delivery_start_at: String
    delivery_end_at: String
    rights_control_type: String
    parents: [Meta]
    push_rank: String
    rating: String
    isAllowedRating: Boolean
    rights: [Right]
    genres: [Genre]
    meta_keywords: [String]
    meta_description: String
    live_flag: Boolean
    video_flag: Boolean
    heroImage: ChannelMetaHeroImage
    contractStatus: Boolean @cacheControl(scope: PRIVATE, maxAge: 0)
    contractAllowed: Boolean
    tabMenu: [Link]
    distributor: String
  }
  union MediaMeta = EpisodeMeta | LiveMeta

  type MetaConnection {
    data: [Meta]
    pageInfo: PageInfo
  }

  type ChannelConnection {
    data: [ChannelMeta]
    pageInfo: PageInfo
  }

  enum EpisodeType {
    LIVE_ONAIR
    LIVE_BEFORE
    CATCHUP
    VIDEO
  }

  type Bookmark @cacheControl(maxAge: 0) {
    meta_id: Int
    bookmark_meta_id: Int
    position: Int
    bookmark_meta: Meta
  }

  type EntitledResult @cacheControl(scope: PRIVATE) {
    data: [Meta]
    pageInfo: PageInfo
  }

  type EntitledEpisodesResult @cacheControl(scope: PRIVATE) {
    data: [MediaMeta]
    pageInfo: PageInfo
  }

  type GenrePaletteEpisodesResult {
    data: [MediaMeta]
    pageInfo: PageInfo
  }

  type ChannelByCourseResult {
    id: Int
    name: String
  }

  type Query {
    episode(id: Int): Meta

    nextEpisode(id: Int): Meta

    children(
      id: Int
      offset: Int
      limit: Int
      filter: JSONObject
      sort: JSONObject
    ): MetaConnection

    channels(
      offset: Int
      limit: Int
      filter: JSONObject
      sort: String
    ): ChannelConnection

    channelsWithContractStatus(
      offset: Int
      limit: Int
      filter: JSONObject
      sort: String
      uuid: String
    ): ChannelConnection
    channelDetail(id: Int): ChannelMeta
    channelFeatureEpisodes(id: Int, offset: Int, limit: Int): MetaConnection

    channelEpisodeSearch(
      keyword: String
      id: Int
      episodeType: EpisodeType
      offset: Int
      limit: Int
    ): MetaConnection

    channelLiveSchedule(
      id: Int
      day: String
      offset: Int
      limit: Int
    ): MetaConnection

    channelLinearEpisodes(
      id: Int
      filter: JSONObject
      offset: Int
      limit: Int
    ): MetaConnection

    search(
      keyword: String
      filter: JSONObject
      sort: String
      offset: Int
      limit: Int
      minScore: Int
    ): MetaConnection

    searchTvShow(
      keyword: String
      filter: JSONObject
      sort: String
      offset: Int
      limit: Int
      minScore: Int
    ): MetaConnection

    title(id: Int): TVShowMeta

    titles(
      filter: JSONObject
      sort: String
      offset: Int
      limit: Int
    ): MetaConnection

    entitled(
      channelId: Int
      contentType: String
      offset: Int
      limit: Int
    ): EntitledResult

    getEntitledStoreEpisodes(offset: Int, limit: Int): EntitledEpisodesResult

    genrePaletteEpisodes(
      channel: Int
      genre: [Int]
      offset: Int
      limit: Int
      sortOrder: String
      type: String
    ): GenrePaletteEpisodesResult
  }
`;

async function getUserRightIds(dataSources, info) {
  info.cacheControl.setCacheHint({ scope: "PRIVATE" });
  const userRightIds = await dataSources.user.rightIds();
  return userRightIds;
}

export const resolvers = {
  Query: {
    episode: async (_parent, { id }, { dataSources }) => {
      const res = await dataSources.episodes.getEpisode(id);
      const meta = res.metas[0];
      return meta;
    },
    nextEpisode: async (_parent, { id }, { dataSources }) => {
      try {
        const meta = await dataSources.episodes.nextEpisode(id);
        // nextがない時は204でbodyがから文字で返ってくる
        if (typeof meta === "string") {
          return null;
        } else {
          return meta;
        }
      } catch (e) {
        return null;
      }
    },
    children: async (_parent, args, { dataSources }) => {
      const res = await dataSources.meta.children(args);
      return {
        data: res.metas,
        pageInfo: createPageInfo({ ...args, count: res.total_count }),
      };
    },
    channels: async (_parent, args, { dataSources }) => {
      const res = await dataSources.meta.channels(args);
      const data = res?.metas;
      return {
        data,
        pageInfo: createPageInfo({ ...args, count: res.total_count }),
      };
    },
    channelsWithContractStatus: async (_parent, args, { dataSources }) => {
      const res = await dataSources.meta.channels(args);
      const data = res?.metas;
      return {
        data,
        pageInfo: createPageInfo({ ...args, count: res.total_count }),
      };
    },
    channelFeatureEpisodes: async (_parent, args, { dataSources }) => {
      const res = await dataSources.meta.channelFeatureEpisodes(args);
      return {
        data: res.metas,
        pageInfo: createPageInfo({ ...args, count: res.total_count }),
      };
    },
    channelDetail: async (_parent, { id }, { dataSources }) => {
      const channel = await dataSources.meta.channelDetail(id);
      return channel;
    },
    channelEpisodeSearch: async (
      _parent,
      { keyword, id, episodeType, offset, limit },
      { dataSources }
    ) => {
      const filter = { "values.channel": id };
      switch (episodeType) {
        case "LIVE_ONAIR":
          filter["values.is_live"] = true;
          filter.time_range = {
            "values.encode_start_date": { lte: "now" },
            "values.encode_end_date": { gt: "now" },
          };
          break;
        case "LIVE_BEFORE":
          filter["values.is_live"] = true;
          filter.time_range = { "values.encode_start_date": { gt: "now" } };
          break;
        case "CATCHUP":
          filter["values.is_live"] = true;
          filter["values.catch_up_flag"] = true;
          filter.time_range = {
            "values.encode_end_date": { lt: "now" },
            delivery_end_at: { gt: "now" },
          };
          break;
        case "VIDEO":
          filter["values.is_live"] = false;
          break;
      }
      const res = await dataSources.meta.searchEpisode({
        keyword,
        filter,
        offset,
        limit,
      });
      return {
        data: res.metas,
        pageInfo: createPageInfo({ offset, limit, count: res.total_count }),
      };
    },
    channelLiveSchedule: async (_parent, args, { dataSources }) => {
      const res = await dataSources.meta.channelLiveSchedule(args);
      return {
        data: res.metas,
        pageInfo: createPageInfo({ ...args, count: res.total_count }),
      };
    },
    channelLinearEpisodes: async (_parent, args, { dataSources }) => {
      const res = await dataSources.meta.channelLinearEpisodes(args);
      return {
        data: res.metas,
        pageInfo: createPageInfo({ ...args, count: res.total_count }),
      };
    },
    search: async (_parent, args, { dataSources }) => {
      const res = await dataSources.meta.searchEpisode(args);
      return {
        data: res.metas,
        pageInfo: createPageInfo({ ...args, count: res.total_count }),
      };
    },
    searchTvShow: async (_parent, args, { dataSources }) => {
      const res = await dataSources.meta.searchTvShow(args);
      return {
        data: res.metas,
        pageInfo: createPageInfo({ ...args, count: res.total_count }),
      };
    },
    title: async (_parent, { id }, { dataSources }) => {
      const title = await dataSources.meta.title(id);
      return title;
    },
    titles: async (_parent, args, { dataSources }) => {
      const res = await dataSources.meta.titles(args);
      return {
        data: res.metas,
        pageInfo: createPageInfo({ ...args, count: res.total_count }),
      };
    },
    entitled: async (_parent, args, { dataSources }) => {
      const res = await dataSources.meta.entitled(args);
      return {
        data: res.metas,
        pageInfo: createPageInfo({ ...args, count: res.total_count }),
      };
    },
    getEntitledStoreEpisodes: async (_parent, args, { dataSources }) => {
      const res = await dataSources.meta.getEntitledStoreEpisodes(args);
      return {
        data: res.metas,
        pageInfo: createPageInfo({ ...args, count: res.total_count }),
      };
    },
    genrePaletteEpisodes: async (_parent, args, { dataSources }) => {
      const res = await dataSources.meta.genrePaletteEpisodes(args);
      return {
        data: res.metas,
        pageInfo: createPageInfo({
          ...args,
          count: res.total_count,
        }),
      };
    },
  },
  Meta: {
    __resolveType: metaResolveType,
  },
  TVShowMeta: {
    thumbnailUrl,
    smallThumbnailUrl,
    mediumThumbnailUrl,
    largeThumbnailUrl,
    name: (obj, _args, { req }) => ratingMask(req, obj, "name"),
    description: (obj, _args, { req }) => ratingMask(req, obj, "description"),
    rights: (obj) => values(obj, "rights"),
    genres,
    rating: (obj) => values(obj, "rating"),
    isAllowedRating: (obj, _args, { req }) => isAllowedRating(req, obj),
    meta_keywords: (obj) => values(obj, "meta_keywords"),
    meta_description: (obj) => values(obj, "meta_description"),
    channel: (obj) => values(obj, "channel"),
    episode_num: (obj) => values(obj, "episode_num"),
    bookmark: async (parent, _args, { dataSources }) => {
      const bookmark = await dataSources.user.viewingBookmarkFromMeta(
        parent.id
      );
      if (bookmark && bookmark.position < bookmark.bookmark_meta.duration) {
        return bookmark;
      } else {
        const children = await dataSources.meta.children({
          id: parent.id,
          filter: {
            range: {
              "values.num": { gt: bookmark?.bookmark_meta?.num || 0 },
            },
          },
          offset: 0,
          limit: 1,
        });
        const meta = children?.metas?.[0];
        if (meta) {
          return {
            meta_id: parent.id,
            bookmark_meta_id: meta.id,
            position: 0,
            bookmark_meta: meta,
          };
        } else {
          return bookmark;
        }
      }
    },
    course: async (meta, _args, { dataSources }) => {
      const right = meta?.values?.rights?.[0];
      if (right?.values?.package_type === PACKAGE_TYPE["svod"]) {
        const courses = await dataSources.right.courses(right.id);
        return courses.courses[0];
      } else {
        return null;
      }
    },
  },
  SeasonMeta: {
    thumbnailUrl,
    smallThumbnailUrl,
    mediumThumbnailUrl,
    largeThumbnailUrl,
    name: (obj, _args, { req }) => ratingMask(req, obj, "name"),
    description: (obj, _args, { req }) => ratingMask(req, obj, "description"),
    rights: (obj) => values(obj, "rights"),
    genres,
    rating: (obj) => values(obj, "rating"),
    isAllowedRating: (obj, _args, { req }) => isAllowedRating(req, obj),
    meta_keywords: (obj) => values(obj, "meta_keywords"),
    meta_description: (obj) => values(obj, "meta_description"),
    channel: (obj) => values(obj, "channel"),
  },
  EpisodeMeta: {
    thumbnailUrl,
    smallThumbnailUrl,
    mediumThumbnailUrl,
    largeThumbnailUrl,
    name: (obj, _args, { req }) => ratingMask(req, obj, "name"),
    description: (obj, _args, { req }) => ratingMask(req, obj, "description"),
    rights: (obj) => values(obj, "rights"),
    genres,
    rating: (obj) => values(obj, "rating"),
    isAllowedRating: (obj, _args, { req }) => isAllowedRating(req, obj),
    meta_keywords: (obj) => values(obj, "meta_keywords"),
    meta_description: (obj) => values(obj, "meta_description"),
    channel: (obj) => values(obj, "channel"),
    area_control_flag: (obj) => values(obj, "area_control_flag"),
    is_live: (obj) => values(obj, "is_live"),
    production_year: (obj) => values(obj, "production_year"),
    distributor: (obj) => values(obj, "distributor"),
    tvShow: (obj) =>
      obj.parents.find((meta) => meta.schema_id == metaSchemas.tvshow.id),
    recommend: (obj) => values(obj, "recommend"),
    source_epg_id: (obj) => values(obj, "source_epg_id"),
    plalaFlag: (obj) => values(obj, "plala_flag"),
    start_over_flag: (obj) => values(obj, "start_over_flag"),
    catch_up_flag: (obj) => values(obj, "catch_up_flag"),
    encode_start_date: (obj) => values(obj, "encode_start_date"),
    encode_end_date: (obj) => values(obj, "encode_end_date"),
  },
  LiveMeta: {
    thumbnailUrl,
    smallThumbnailUrl,
    mediumThumbnailUrl,
    largeThumbnailUrl,
    name: (obj, _args, { req }) => ratingMask(req, obj, "name"),
    description: (obj, _args, { req }) => ratingMask(req, obj, "description"),
    rights: (obj) => values(obj, "rights"),
    genres,
    rating: (obj) => values(obj, "rating"),
    isAllowedRating: (obj, _args, { req }) => isAllowedRating(req, obj),
    meta_keywords: (obj) => values(obj, "meta_keywords"),
    meta_description: (obj) => values(obj, "meta_description"),
    channel: (obj) => values(obj, "channel"),
    area_control_flag: (obj) => values(obj, "area_control_flag"),
    is_live: (obj) => values(obj, "is_live"),
    production_year: (obj) => values(obj, "production_year"),
    distributor: (obj) => values(obj, "distributor"),
    start_over_flag: (obj) => values(obj, "start_over_flag"),
    catch_up_flag: (obj) => values(obj, "catch_up_flag"),
    encode_start_date: (obj) => values(obj, "encode_start_date"),
    encode_end_date: (obj) => values(obj, "encode_end_date"),
    tvShow: (obj) =>
      obj.parents.find((meta) => meta.schema_id == metaSchemas.tvshow.id),
    recommend: (obj) => values(obj, "recommend"),
    source_epg_id: (obj) => values(obj, "source_epg_id"),
    plalaFlag: (obj) => values(obj, "plala_flag"),
  },
  ChannelMeta: {
    thumbnailUrl,
    smallThumbnailUrl,
    mediumThumbnailUrl,
    largeThumbnailUrl,
    name: (obj, _args, { req }) => ratingMask(req, obj, "name"),
    description: (obj, _args, { req }) => ratingMask(req, obj, "description"),
    rights: (obj) => values(obj, "rights"),
    genres,
    rating: (obj) => values(obj, "rating"),
    isAllowedRating: (obj, _args, { req }) => isAllowedRating(req, obj),
    meta_keywords: (obj) => values(obj, "meta_keywords"),
    meta_description: (obj) => values(obj, "meta_description"),
    live_flag: (obj) => values(obj, "live_flag"),
    video_flag: (obj) => values(obj, "video_flag"),
    heroImage: (obj) => obj.hero_image,
    distributor: (obj) => values(obj, "distributor"),
    contractStatus: async (parent, _args, { req, dataSources }, info) => {
      if (req.userToken) {
        const userRightIds = await getUserRightIds(dataSources, info);
        const rights = parent.values.rights;
        const contract = rights.find((right) =>
          userRightIds.includes(right.id)
        );
        return !!contract;
      }
      return false;
    },
    contractAllowed: (channel) => {
      const right = channel?.values?.rights?.[0];
      if (right) {
        return isDateRangeIn(
          right?.values?.delivery_start_date,
          right?.values?.delivery_end_date,
          { buffer: CONTRACTABLE_BUFFER, default: true }
        );
      }
      return false;
    },
    tabMenu: async (parent, _args, { dataSources }) => {
      if (parent?.values?.detail_tab_menu) {
        const res = await dataSources.linkSet.linkSet(
          parent?.values?.detail_tab_menu
        );
        return res?.links;
      }
    },
  },
  MediaMeta: {
    __resolveType: metaResolveType,
  },
};
